import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ProjectCustomDomainInfo,
  ProjectCustomDomainSessionOption,
} from '@claude-code-api/shared';
import {
  CheckCircle2,
  ExternalLink,
  Globe2,
  Link2,
  Pencil,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@/components/ui';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { UnsavedChangesDialog } from '@/components/shared/UnsavedChangesDialog';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { ProjectCustomDomainHelpDialog } from './ProjectCustomDomainHelpDialog';
import { ProjectCustomDomainHealth } from './ProjectCustomDomainHealth';
import { ProjectCustomDomainFlow } from './ProjectCustomDomainFlow';

interface ProjectCustomDomainCardProps {
  projectId: string;
  projectName: string;
  sessions: readonly ProjectCustomDomainSessionOption[];
  embedded?: boolean;
}

interface CustomDomainApiResponse {
  success: boolean;
  enabled?: boolean;
  data?: ProjectCustomDomainInfo | null;
  error?: string;
}

interface DomainEditSnapshot {
  hostname: string;
  sessionId: string;
}

async function readResponse(response: Response): Promise<CustomDomainApiResponse> {
  return response.json() as Promise<CustomDomainApiResponse>;
}

export function ProjectCustomDomainCard({
  projectId,
  projectName,
  sessions,
  embedded = false,
}: ProjectCustomDomainCardProps) {
  const { t } = useTranslation(['projects', 'common']);
  const [domain, setDomain] = useState<ProjectCustomDomainInfo | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [hostname, setHostname] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [editBaseline, setEditBaseline] = useState<DomainEditSnapshot>({
    hostname: '',
    sessionId: '',
  });
  const [saving, setSaving] = useState(false);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const checkingDomainRef = useRef(false);
  const editDirty = hostname !== editBaseline.hostname || sessionId !== editBaseline.sessionId;

  const closeEditorWithoutSaving = () => {
    setHostname(editBaseline.hostname);
    setSessionId(editBaseline.sessionId);
    setEditOpen(false);
  };
  const unsavedChanges = useUnsavedChangesGuard({
    dirty: editDirty,
    onDiscard: closeEditorWithoutSaving,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/projects/${projectId}/custom-domain`, {
          credentials: 'include',
        });
        const result = await readResponse(response);
        if (!response.ok) throw new Error(result.error ?? t('projects:deployments.customDomain.errors.load'));
        if (cancelled) return;
        setEnabled(result.enabled === true);
        setDomain(result.data ?? null);
      } catch (error) {
        if (!cancelled) {
          toast.error(error instanceof Error ? error.message : t('projects:deployments.customDomain.errors.load'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId, t]);

  const startEditing = () => {
    const snapshot = {
      hostname: domain?.hostname ?? '',
      sessionId: domain?.sessionId ?? '',
    };
    setEditBaseline(snapshot);
    setHostname(snapshot.hostname);
    setSessionId(snapshot.sessionId);
    setEditOpen(true);
  };

  const setEditorOpen = (open: boolean) => {
    if (saving) return;
    if (open) {
      setEditOpen(true);
      return;
    }
    unsavedChanges.requestClose();
  };

  const setConfigurationOpen = (open: boolean) => {
    if (saving || removing) return;
    if (!open) {
      if (editOpen) {
        unsavedChanges.requestClose();
        return;
      }
      setConfigOpen(false);
      return;
    }
    setConfigOpen(true);
    if (!domain) {
      startEditing();
      return;
    }
    void verify({ silent: true });
  };

  const save = async () => {
    if (!hostname.trim() || !sessionId) return;
    setSaving(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/custom-domain`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostname: hostname.trim(), sessionId }),
      });
      const result = await readResponse(response);
      if (!response.ok || !result.data) {
        throw new Error(result.error ?? t('projects:deployments.customDomain.errors.save'));
      }
      setDomain(result.data);
      setEditBaseline({
        hostname: result.data.hostname,
        sessionId: result.data.sessionId ?? '',
      });
      setEditOpen(false);
      toast.success(t('projects:deployments.customDomain.messages.saved'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('projects:deployments.customDomain.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const verify = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (checkingDomainRef.current) return;
    checkingDomainRef.current = true;
    setCheckingDomain(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/custom-domain/verify`, {
        method: 'POST',
        credentials: 'include',
      });
      const result = await readResponse(response);
      if (!response.ok || !result.data) {
        throw new Error(result.error ?? t('projects:deployments.customDomain.errors.verify'));
      }
      setDomain(result.data);
      if (!silent && result.data.status === 'active') {
        toast.success(t('projects:deployments.customDomain.messages.verified'));
      } else if (!silent && result.data.lastError) {
        toast.error(result.data.lastError);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('projects:deployments.customDomain.errors.verify'));
    } finally {
      checkingDomainRef.current = false;
      setCheckingDomain(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/custom-domain`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const result = await readResponse(response);
      if (!response.ok) throw new Error(result.error ?? t('projects:deployments.customDomain.errors.remove'));
      setDomain(null);
      setEditOpen(false);
      setConfigOpen(false);
      setConfirmRemove(false);
      setHostname('');
      setSessionId('');
      toast.success(t('projects:deployments.customDomain.messages.removed'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('projects:deployments.customDomain.errors.remove'));
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <Card className={embedded ? 'rounded-none border-0 bg-transparent shadow-none' : 'border-dashed'}>
        <CardContent className="flex items-center justify-center py-10">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  const isLive = (domain?.status === 'active' || domain?.status === 'degraded')
    && domain.targetStatus === 'running';
  const selectedSession = sessions.find(
    (session) => session.id === (sessionId || domain?.sessionId),
  );
  const currentUrl = selectedSession?.currentUrl ?? null;
  const statusVariant = isLive
    ? 'success'
    : domain?.status === 'suspended' || domain?.targetStatus === 'missing'
      ? 'destructive'
      : 'warning';

  return (
    <>
      <Card className={embedded
        ? 'rounded-none border-0 bg-transparent shadow-none'
        : 'border-primary/20 bg-gradient-to-r from-card via-card to-primary/5'}>
        <CardContent className={embedded
          ? 'flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between'
          : 'flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between'}>
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
              <Globe2 className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-muted-foreground">
                {t('projects:deployments.customDomain.title')}
              </p>
              <p className="mt-0.5 truncate font-mono text-sm font-medium sm:text-base">
                {domain?.hostname
                  ?? (enabled
                    ? t('projects:deployments.customDomain.notConfigured')
                    : t('projects:deployments.customDomain.unavailableTitle'))}
              </p>
            </div>
          </div>
          <Button
            variant={domain ? 'outline' : 'default'}
            size="sm"
            className="w-full sm:w-auto"
            disabled={!enabled}
            onClick={() => setConfigurationOpen(true)}
          >
            {domain ? <Pencil className="mr-1.5 h-3.5 w-3.5" /> : <Link2 className="mr-1.5 h-3.5 w-3.5" />}
            {t('projects:deployments.customDomain.openConfig')}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={configOpen} onOpenChange={setConfigurationOpen}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-3xl flex-col overflow-hidden p-0">
          <div className="border-b border-border/70 bg-gradient-to-r from-card via-card to-primary/5 px-6 py-5 pr-12">
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2">
                {t('projects:deployments.customDomain.title')}
                {domain && (
                  <Badge variant={statusVariant}>
                    {t(`projects:deployments.customDomain.status.${domain.status}`)}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                {t('projects:deployments.customDomain.description')}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            {isLive && domain && (
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-success">
                  <CheckCircle2 className="h-5 w-5 shrink-0" />
                  <p className="font-medium">{t('projects:deployments.customDomain.activeTitle')}</p>
                </div>
                <Button asChild size="sm">
                  <a href={`https://${domain.hostname}`} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                    {t('projects:deployments.customDomain.open')}
                  </a>
                </Button>
              </div>
            )}
            <div className="mb-4 flex flex-wrap justify-end gap-2">
              <ProjectCustomDomainHelpDialog domain={domain} currentUrl={currentUrl} />
              <Button variant="outline" size="sm" onClick={startEditing}>
                {domain ? <Pencil className="mr-1.5 h-3.5 w-3.5" /> : <Link2 className="mr-1.5 h-3.5 w-3.5" />}
                {domain
                  ? t('projects:deployments.customDomain.change')
                  : t('projects:deployments.customDomain.connect')}
              </Button>
            </div>
          {domain ? (
            <div className="space-y-5">
              <ProjectCustomDomainFlow domain={domain} checking={checkingDomain} projectName={projectName} />

              <ProjectCustomDomainHealth domain={domain} />

              {!isLive && (
                domain.status !== 'active' ? (
                  <div className="flex items-start gap-2 rounded-lg border border-warning/25 bg-warning/10 p-3 text-sm">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                    <div>
                      <p className="font-medium">{t('projects:deployments.customDomain.waitingTitle')}</p>
                      <p className="text-muted-foreground">
                        {domain.lastError ?? t('projects:deployments.customDomain.waitingDescription')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg border border-warning/25 bg-warning/10 p-3 text-sm">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                    <div>
                      <p className="font-medium">
                        {t(`projects:deployments.customDomain.targetStatus.${domain.targetStatus}.title`)}
                      </p>
                      <p className="text-muted-foreground">
                        {t(`projects:deployments.customDomain.targetStatus.${domain.targetStatus}.description`)}
                      </p>
                    </div>
                  </div>
                )
              )}

              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setConfirmRemove(true)}>
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  {t('projects:deployments.customDomain.remove')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-start justify-between gap-4 rounded-xl border border-dashed bg-background/60 p-5 sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <Link2 className="mt-0.5 h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{t('projects:deployments.customDomain.emptyTitle')}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('projects:deployments.customDomain.emptyDescription')}
                  </p>
                </div>
              </div>
              <Button onClick={startEditing}>
                {t('projects:deployments.customDomain.connect')}
              </Button>
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {domain
                ? t('projects:deployments.customDomain.editTitle')
                : t('projects:deployments.customDomain.connectTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('projects:deployments.customDomain.subdomainOnly')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="custom-domain-hostname">
                {t('projects:deployments.customDomain.hostname')}
              </Label>
              <Input
                id="custom-domain-hostname"
                value={hostname}
                onChange={(event) => setHostname(event.target.value)}
                placeholder={t('projects:deployments.customDomain.hostnamePlaceholder')}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="custom-domain-session">
                {t('projects:deployments.customDomain.targetSession')}
              </Label>
              <Select value={sessionId} onValueChange={setSessionId}>
                <SelectTrigger id="custom-domain-session">
                  <SelectValue placeholder={t('projects:deployments.customDomain.selectSession')} />
                </SelectTrigger>
                <SelectContent>
                  {sessions.map((session) => (
                    <SelectItem key={session.id} value={session.id}>
                      {session.runnerType} · {session.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sessions.length === 0 && (
                <p className="text-xs text-warning">
                  {t('projects:deployments.customDomain.noRunningSession')}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={unsavedChanges.requestClose} disabled={saving}>
              {t('common:cancel')}
            </Button>
            <Button onClick={() => void save()} disabled={saving || !hostname.trim() || !sessionId}>
              {saving && <Spinner size="sm" />}
              {t('common:save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={unsavedChanges.confirmationOpen}
        onOpenChange={unsavedChanges.setConfirmationOpen}
        onDiscard={unsavedChanges.confirmDiscard}
        title={t('projects:deployments.customDomain.unsaved.title')}
        description={t('projects:deployments.customDomain.unsaved.description')}
        discardLabel={t('projects:deployments.customDomain.unsaved.discard')}
        continueLabel={t('projects:deployments.customDomain.unsaved.continue')}
      />

      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={t('projects:deployments.customDomain.removeTitle')}
        description={t('projects:deployments.customDomain.removeDescription')}
        confirmLabel={t('projects:deployments.customDomain.remove')}
        cancelLabel={t('common:cancel')}
        onConfirm={() => void remove()}
        loading={removing}
      />
    </>
  );
}
